// The pause menu — a small site, not a dialog (PLAN-UI §28.3).
//
// It absorbs what used to be a top bar and a `View ▾` menu: Settings, Rules,
// Costs, Powers, Feedback, Restart, Main menu, with the diagnostics line at the
// foot of the nav. Two columns, because seven screens in one column is a list
// you scroll and a nav you can aim at is not.
//
// **It does not pause the game.** Nothing here dispatches a command, the clock
// is untouched, and the copy never says "paused" — the title is "Menu". The
// button that opens it is a pause glyph because that is the gesture people
// know, and that is as far as the metaphor goes.
//
// **`keepMounted` is load-bearing.** `live-smoke.js` reads `move-count` and
// `state-hash` off the page without opening anything, so the footer has to be
// in the DOM while the menu is closed. Only the footer: a closed menu renders
// no screen at all, so the rules chunk is not fetched and the feedback form
// snapshots no board until somebody asks for one.

import type { CameraPrefs } from "./cameraPrefs";
import { CostsContent } from "./CostsDialog";
import { FeedbackForm, type FeedbackFormProps } from "./FeedbackDialog";
import { Btn, Modal, PauseNavButton, PauseShell, styled, TOKENS } from "./kit";
import { type PowerRow, PowersScreen } from "./pause/PowersScreen";
import { restartNoun } from "./pause/restart";
import { SettingsScreen } from "./pause/SettingsScreen";
import { Copy, ScreenTitle } from "./pause/text";
import { RulesDocument } from "./RulesPage";

export type PauseScreen =
  | "settings"
  | "rules"
  | "costs"
  | "powers"
  | "feedback"
  | "restart"
  | "menu";

export interface PauseMenuProps {
  /** Which screen is showing, or `null` for closed. */
  screen: PauseScreen | null;
  onScreen(screen: PauseScreen | null): void;

  /** In a run: the Powers screen lists, and leaving keeps the save. */
  inRun: boolean;
  /** What the seat holds — never the side to move. Null outside a run. */
  powers: readonly PowerRow[] | null;
  /**
   * Watching a game rather than playing one.
   *
   * **Nothing here changes for it, on purpose.** Watching is a whole seat and
   * the transport that replaces the player's controls lives on the board's own
   * chrome (§28.6); the menu is settings, reference and the way out, and a
   * spectator wants all three. Carried so a screen that ever does need it does
   * not have to change the contract.
   */
  watching: boolean;

  camera: CameraPrefs;
  onCamera(patch: Partial<CameraPrefs>): void;
  cameraFixed: boolean;
  onResetView(): void;

  /** A heading for the Rules screen to open at, from a hint. */
  rulesAnchor?: string;

  /** The form's own props, less what the menu supplies: the frame and `onDone`. */
  feedback: Omit<FeedbackFormProps, "onDone">;

  /** "Restart level", "New run", "New game" — the caller knows which. */
  restartLabel: string;
  onRestart?(): void;
  onExit?(): void;

  plies: number;
  hash: string;
  build: string;
}

/** The five screens that are just screens, in nav order. */
const NAV: readonly { id: PauseScreen; label: string; testId: string }[] = [
  { id: "settings", label: "Settings", testId: "pause-settings" },
  // `hud-rules`, `hud-costs` and `hud-feedback` were the old header buttons.
  // The ids survive the move because test ids are API (PLAN-UI §28.0).
  { id: "rules", label: "Rules", testId: "hud-rules" },
  { id: "costs", label: "Costs", testId: "hud-costs" },
  { id: "powers", label: "Powers", testId: "pause-powers" },
  { id: "feedback", label: "Feedback", testId: "hud-feedback" }
];

/**
 * The front door's own logo, sized to the nav (`/brand/logo.png`, 960×538 —
 * see `tools/buildBrand.mjs`). One mark for the game, not a second one set in
 * a placeholder face (designer ruling, U3 review). `aspectRatio` reserves the
 * height so the nav does not jump when the image decodes.
 */
const Logo = styled("img")(({ theme }) => ({
  display: "block",
  width: `calc(100% - ${2 * (theme.kit.space.xl - 4)}px)`,
  aspectRatio: "960 / 538",
  margin: `0 auto ${theme.kit.space.md}px`,
  filter: `drop-shadow(0 2px 10px color-mix(in srgb, ${theme.kit.color.shadow} 55%, transparent))`
}));

const Spacer = styled("div")({ flex: 1 });

/**
 * Plies, hash, build — the line a bug report quotes.
 *
 * At the foot of the nav rather than in the body, so it does not scroll away
 * with whichever screen is up and is in the same place on all seven.
 */
const Diagnostics = styled("div")(({ theme }) => ({
  marginTop: theme.kit.space.md,
  padding: `${theme.kit.space.md}px ${theme.kit.space.xl - 4}px 0`,
  borderTop: `1px solid ${theme.kit.color.line}`,
  fontFamily: theme.kit.font.display,
  fontWeight: 600,
  fontSize: 10.5,
  letterSpacing: ".05em",
  color: theme.kit.color.inkDim,
  opacity: 0.7,
  wordBreak: "break-all"
}));

export function PauseMenu(props: PauseMenuProps) {
  const { screen, onScreen, restartLabel } = props;
  const close = () => onScreen(null);

  const nav = (
    <>
      <Logo src="/brand/logo.png" alt="Factory Chess" />
      {NAV.map((entry) => (
        <PauseNavButton
          key={entry.id}
          type="button"
          data-testid={entry.testId}
          on={screen === entry.id}
          onClick={() => onScreen(entry.id)}
        >
          {entry.label}
        </PauseNavButton>
      ))}
      <Spacer />
      {/*
        Restart and Main menu are *screens* with a confirm on them, not actions.
        A mis-click on a nav row must not end a run, and the two rows that could
        are the two sitting alone at the bottom of the list.
      */}
      <PauseNavButton
        type="button"
        data-testid="pause-restart"
        on={screen === "restart"}
        onClick={() => onScreen("restart")}
      >
        {restartLabel}
      </PauseNavButton>
      <PauseNavButton
        type="button"
        exit
        data-testid="to-menu"
        on={screen === "menu"}
        onClick={() => onScreen("menu")}
      >
        Main menu
      </PauseNavButton>
      <Diagnostics>
        {/* "N plies" is a format, not prose — `live-smoke.js` splits on the
            space and takes the number. */}
        <span data-testid="move-count">{props.plies} plies</span> ·{" "}
        <span data-testid="state-hash">{props.hash}</span> ·{" "}
        <span>{props.build}</span>
      </Diagnostics>
    </>
  );

  return (
    <Modal
      open={screen !== null}
      onClose={close}
      keepMounted
      // **Entry only.** A view action closes this to look at the board, and a
      // backdrop fading out for 200ms eats the click that follows — the
      // interaction smoke rotated the camera and then moved a piece into the
      // fade. The kit's `Popover` makes the same call for the same reason.
      transitionDuration={{ enter: TOKENS.motion.duration.fast, exit: 0 }}
      maxWidth={false}
      data-testid="pause"
      sx={(theme) => ({
        // Below `modal`, so a celebration or a run-over card stands over the
        // menu rather than under it — those are moments the game is waiting on.
        zIndex: theme.kit.z.pause,
        // The plate is `PauseShell`; the paper is only the focus trap's box.
        "& .MuiDialog-paper": {
          background: "none",
          border: 0,
          borderRadius: 0,
          boxShadow: "none",
          margin: 0,
          maxWidth: "none",
          maxHeight: "none",
          overflow: "visible"
        }
      })}
    >
      <PauseShell nav={nav}>{screen && <Screens {...props} />}</PauseShell>
    </Modal>
  );
}

/** The body. Mounted only while a screen is showing — see `keepMounted` above. */
function Screens(props: PauseMenuProps): React.ReactElement | null {
  const { screen, onScreen, inRun, restartLabel, onRestart, onExit } = props;
  const close = () => onScreen(null);

  switch (screen) {
    case null:
      return null;

    case "settings":
      return (
        <SettingsScreen
          camera={props.camera}
          onCamera={props.onCamera}
          cameraFixed={props.cameraFixed}
          onResetView={props.onResetView}
          onClose={close}
        />
      );

    // No title of its own: the document opens with one, and a heading over a
    // heading is the pause menu shouting the nav row back at you.
    case "rules":
      return (
        <div data-testid="rules-screen">
          <RulesDocument anchor={props.rulesAnchor} />
        </div>
      );

    case "costs":
      return (
        <div data-testid="costs-dialog">
          <ScreenTitle>Costs &amp; numbers</ScreenTitle>
          <CostsContent />
        </div>
      );

    case "powers":
      return <PowersScreen powers={props.powers} />;

    case "feedback":
      return (
        <div data-testid="pause-screen-feedback">
          <ScreenTitle>Feedback</ScreenTitle>
          <FeedbackForm {...props.feedback} onDone={close} />
        </div>
      );

    case "restart":
      return (
        <div data-testid="pause-screen-restart">
          <ScreenTitle>{restartLabel}</ScreenTitle>
          <Copy>
            Start this {restartNoun(restartLabel)} again from the beginning.
          </Copy>
          <Btn
            variant="danger"
            data-testid="new-game"
            disabled={!onRestart}
            onClick={() => {
              onRestart?.();
              close();
            }}
          >
            {restartLabel}
          </Btn>
        </div>
      );

    case "menu":
      return (
        <div data-testid="pause-screen-menu">
          <ScreenTitle>Main menu</ScreenTitle>
          <Copy>
            {inRun
              ? "Leave to the main menu. A run keeps its save."
              : "Leave to the main menu."}
          </Copy>
          {/* No `close()` after it: leaving unmounts the whole game screen, and
              closing a menu on the way out is a frame of the board nobody
              asked to see again. */}
          <Btn
            variant="danger"
            data-testid="leave-game"
            disabled={!onExit}
            onClick={() => onExit?.()}
          >
            Main menu
          </Btn>
        </div>
      );
  }
}
